/**
 * D.3.5 — Inline provisioning panel mounted by NipePortableSiteDropDialog
 * after the user picks SharePoint Site / Both.
 *
 * Flow:
 *   1. Title prompt (pre-filled with "<source label> Vault").
 *   2. On "Start provisioning":
 *      - Acquire MSAL Graph token (popup if needed).
 *      - Call `provisionFromPortableSite` mutation → returns jobId + slug + viaDemoToggle.
 *      - Call `kickVaultExportProvisioner` action → POSTs to Function App.
 *   3. Subscribe to `getVaultExportJob({jobId})`. Render progress bar.
 *   4. On status='complete': show site URL + "Open in new tab" button.
 *   5. On status='failed': show error + "Retry" (re-enters phase 1).
 *
 * Win95 chrome; Tailwind classes only (memory feedback_use_tailwind).
 * No light-grey text on light backgrounds (memory feedback_no_light_grey_text).
 */

import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import { testIds } from '../../lib/testIds';
import type { PortableSite } from '../../lib/portable-site';
import {
  acquireGraphToken,
  connectMicrosoftAccountRedirect,
  getActiveAccount,
  getMsalAccountTenantId,
  getMsalApp,
} from '../../lib/graph/msalClient';

/** sessionStorage key for the post-MSAL-redirect resume payload. */
export const VAULT_DROP_RESUME_KEY = 'repancy:vault-drop-provision-resume';

export interface VaultDropResumePayload {
  /** Serialized PortableSite (we re-hydrate via JSON.parse). */
  siteJson: string;
  sourceLabel: string;
  title: string;
  /** Wall-clock to expire stale resumes (older than 10 min). */
  stashedAt: number;
}

function stashForResume(payload: VaultDropResumePayload): void {
  try {
    sessionStorage.setItem(VAULT_DROP_RESUME_KEY, JSON.stringify(payload));
  } catch {
    /* sessionStorage full or unavailable */
  }
}

export function peekVaultDropResume(): VaultDropResumePayload | null {
  try {
    const raw = sessionStorage.getItem(VAULT_DROP_RESUME_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as VaultDropResumePayload;
    if (Date.now() - parsed.stashedAt > 10 * 60 * 1000) {
      sessionStorage.removeItem(VAULT_DROP_RESUME_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function consumeVaultDropResume(): VaultDropResumePayload | null {
  const payload = peekVaultDropResume();
  if (payload !== null) {
    try {
      sessionStorage.removeItem(VAULT_DROP_RESUME_KEY);
    } catch {
      /* ignore */
    }
  }
  return payload;
}
import { useShowcaseSessionId } from '../../lib/showcase/showcaseSession';
import { useShowcaseVisible } from '../../lib/showcase/useShowcaseVisible';
import {
  markVaultDropProvisionStartFired,
  setVaultDropFinalStatus,
} from '../../lib/test-hooks';

interface VaultDropProvisionPanelProps {
  site: PortableSite;
  sourceLabel: string;
  /** Called when the user dismisses the panel (cancel button or success-then-close). */
  onClose: () => void;
}

interface SubmitState {
  jobId: Id<'portalProvisioningJobs'> | null;
  viaDemoToggle: boolean;
  starting: boolean;
  startError: string | null;
  needsMsConnect: boolean;
}

export function extractSiteUrl(resultJson: string | undefined): string | null {
  if (!resultJson) return null;
  try {
    const parsed = JSON.parse(resultJson) as { siteUrl?: unknown };
    if (typeof parsed.siteUrl === 'string' && parsed.siteUrl.length > 0) {
      return parsed.siteUrl;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Pure: shapes the SPA-side PortableSite to match the Convex
 * `provisionFromPortableSite` arg validator. Extracted as a pure helper
 * so it can be unit-tested without rendering the panel.
 */
export function mapPortableSiteForConvex(site: PortableSite): {
  source: string;
  schemaProfile: string;
  notes: { path: string; body: string; frontmatter: string }[];
  assets: { path: string; mimeType: string; sizeBytes: number }[];
  flows: { path: string; body: string }[];
} {
  return {
    source: site.source,
    schemaProfile: site.schemaProfile,
    notes: site.notes.map((n) => ({
      path: n.path,
      body: n.body,
      frontmatter: JSON.stringify(n.frontmatter ?? {}),
    })),
    assets: site.assets.map((a) => ({
      path: a.path,
      mimeType: a.mimeType ?? 'application/octet-stream',
      sizeBytes: a.sizeBytes,
    })),
    flows: site.flows.map((f) => ({ path: f.path, body: f.json })),
  };
}

export function VaultDropProvisionPanel(
  props: VaultDropProvisionPanelProps,
): ReactElement {
  const { site, sourceLabel, onClose } = props;
  const [title, setTitle] = useState(`${sourceLabel} Vault`);
  const [submit, setSubmit] = useState<SubmitState>({
    jobId: null,
    viaDemoToggle: false,
    starting: false,
    startError: null,
    needsMsConnect: false,
  });

  const provisionMutation = useMutation(
    api.vaultExportProvisioning.provisionFromPortableSite,
  );
  const kickAction = useAction(
    api.vaultExportProvisioning.kickVaultExportProvisioner,
  );

  const [isDemo] = useShowcaseVisible();
  const showcaseSessionId = useShowcaseSessionId();

  const job = useQuery(
    api.vaultExportProvisioning.getVaultExportJob,
    submit.jobId !== null ? { jobId: submit.jobId } : 'skip',
  );

  const siteUrl = useMemo(
    () => (job ? extractSiteUrl(job.resultJson) : null),
    [job],
  );

  async function ensureMsAccount(opts: {
    title: string;
  }): Promise<{ token: string; tenantId: string }> {
    if (getActiveAccount() === null) {
      // F16.L-style: skip the popup entirely. Chrome's COOP strips
      // window.opener after navigating through login.microsoftonline.com,
      // which breaks MSAL's popup-bridge and surfaces as
      // `BrowserAuthError: timed_out`. Stash the dropped site + title in
      // sessionStorage, full-page-redirect to Microsoft, return here,
      // resume from stash.
      stashForResume({
        siteJson: JSON.stringify(site),
        sourceLabel,
        title: opts.title,
        stashedAt: Date.now(),
      });
      await connectMicrosoftAccountRedirect();
      // Unreachable in the normal flow (page navigated away). Throw a
      // sentinel just in case so the caller's try/catch tidies up state.
      throw new Error('redirect-navigated');
    }
    const token = await acquireGraphToken();
    const tenantId = getMsalAccountTenantId();
    if (tenantId === null) {
      throw new Error(
        'Microsoft sign-in returned no tenant ID. Sign out and back in.',
      );
    }
    return { token, tenantId };
  }

  async function startProvision() {
    markVaultDropProvisionStartFired();
    const trimmedTitle = title.trim();
    if (trimmedTitle.length === 0) {
      setSubmit((s) => ({ ...s, startError: 'Title is required.' }));
      return;
    }
    setSubmit((s) => ({
      ...s,
      starting: true,
      startError: null,
      needsMsConnect: false,
    }));
    try {
      const { token: graphAccessToken, tenantId: operatorTenantId } =
        await ensureMsAccount({ title: trimmedTitle });

      const result = await provisionMutation({
        site: mapPortableSiteForConvex(site),
        title: trimmedTitle,
        sourceLabel,
        ...(isDemo ? { demoSessionId: showcaseSessionId } : {}),
      });

      if (!result.ok) {
        setSubmit((s) => ({
          ...s,
          starting: false,
          startError: result.reason,
        }));
        return;
      }

      const newJobId = result.jobId as Id<'portalProvisioningJobs'>;
      const viaDemoToggle = result.viaDemoToggle;

      const kick = await kickAction({
        jobId: newJobId,
        graphAccessToken,
        operatorTenantId,
      });
      if (!kick.ok) {
        setSubmit((s) => ({
          ...s,
          starting: false,
          jobId: newJobId,
          viaDemoToggle,
          startError: kick.reason,
        }));
        return;
      }
      setSubmit({
        jobId: newJobId,
        viaDemoToggle,
        starting: false,
        startError: null,
        needsMsConnect: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const needsMs =
        message.includes('No Microsoft account connected') ||
        message.includes('user_cancelled') ||
        message.includes('AADSTS') ||
        message.includes('timed_out') ||
        message.includes('interaction_in_progress');
      // Surface MSAL diagnostic data alongside the user-facing message so
      // a stuck "Starting…" never silently consumes the failure.
      // eslint-disable-next-line no-console
      console.error('[VaultDropProvisionPanel] startProvision failed:', err);
      setSubmit((s) => ({
        ...s,
        starting: false,
        startError: message,
        needsMsConnect: needsMs,
      }));
    }
  }

  function retry() {
    setSubmit({
      jobId: null,
      viaDemoToggle: false,
      starting: false,
      startError: null,
      needsMsConnect: false,
    });
  }

  // Auto-close when status hits complete AND the user clicks "Done".
  // For now we keep the panel open after complete so the user has time
  // to click the URL.
  useEffect(() => {
    if (job?.status === 'complete') {
      setVaultDropFinalStatus('complete');
    } else if (job?.status === 'failed') {
      setVaultDropFinalStatus('failed');
    }
  }, [job?.status]);

  // Post-MSAL-redirect resume. On mount, if there's a stashed payload AND
  // an MS account is now active, restore the user's title + auto-trigger
  // the provisioning flow as if they'd just clicked Start. The dialog's
  // parent component (NipePortableSiteDropDialog) is responsible for
  // reconstituting the dropped site BEFORE this panel mounts; see its
  // own resume useEffect.
  //
  // We must AWAIT getMsalApp() before checking getActiveAccount() — MSAL
  // init runs handleRedirectPromise asynchronously, and the panel mounts
  // before that promise resolves. Without the await, the redirect just
  // completed but getActiveAccount() still returns null, so resume bails
  // out and the dropped site is silently lost.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const peeked = consumeVaultDropResume();
      if (peeked === null) return;
      await getMsalApp(); // ensures handleRedirectPromise has finished
      if (cancelled) return;
      if (getActiveAccount() === null) {
        // MSAL init ran but no account got set — sign-in failed silently.
        // Surface this to the user instead of dropping their work.
        setSubmit((s) => ({
          ...s,
          startError:
            'Microsoft sign-in did not complete. Click Start provisioning to try again.',
        }));
        setTitle(peeked.title);
        return;
      }
      setTitle(peeked.title);
      setTimeout(() => {
        if (!cancelled) void startProvision();
      }, 0);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ──────── Phase 1: title prompt ────────
  if (submit.jobId === null) {
    return (
      <div
        data-testid={testIds.nipe.vaultDropProvisionPanel}
        className="mt-3 border border-[#7f9db9] bg-[#ece9d8] p-3 text-[13px] text-black"
      >
        <p className="font-bold mb-2">Create a new SharePoint site</p>
        <label className="block mb-2">
          <span className="block text-[12px] font-bold mb-1">Site title</span>
          <input
            type="text"
            data-testid={testIds.nipe.vaultDropProvisionTitle}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={submit.starting}
            className="w-full border border-black bg-white px-2 py-1 text-[13px] text-black disabled:opacity-50"
            maxLength={80}
          />
        </label>
        <div className="flex gap-2 mb-1">
          <button
            type="button"
            data-testid={testIds.nipe.vaultDropProvisionStart}
            onClick={() => void startProvision()}
            disabled={submit.starting}
            className="px-3 py-1 bg-[#d0d0d0] border border-[#404040] text-black font-bold cursor-pointer hover:bg-[#e0e0e0] disabled:opacity-50"
          >
            {submit.starting ? 'Starting…' : 'Start provisioning'}
          </button>
          <button
            type="button"
            data-testid={testIds.nipe.vaultDropProvisionCancel}
            onClick={onClose}
            disabled={submit.starting}
            className="px-3 py-1 bg-[#ece9d8] border border-[#7f9db9] text-black disabled:opacity-50"
          >
            Cancel
          </button>
          {submit.needsMsConnect && (
            <button
              type="button"
              onClick={() => {
                // Same redirect-based flow used by startProvision so the
                // user gets the popup-free path. We stash before redirect
                // so the panel can resume after return.
                stashForResume({
                  siteJson: JSON.stringify(site),
                  sourceLabel,
                  title: title.trim() || `${sourceLabel} Vault`,
                  stashedAt: Date.now(),
                });
                void connectMicrosoftAccountRedirect();
              }}
              className="px-3 py-1 bg-[#ece9d8] border border-[#7f9db9] text-black"
            >
              Sign in to Microsoft
            </button>
          )}
        </div>
        {submit.startError !== null && (
          <p
            data-testid={testIds.nipe.vaultDropProvisionError}
            className="text-red-800 text-[12px] mt-1"
          >
            {submit.startError}
          </p>
        )}
      </div>
    );
  }

  // ──────── Phase 2: progress + result ────────
  const pct =
    job && job.stepsTotal > 0
      ? Math.round((job.stepsCompleted / job.stepsTotal) * 100)
      : 0;

  return (
    <div
      data-testid={testIds.nipe.vaultDropProvisionPanel}
      data-job-status={job?.status ?? 'loading'}
      className="mt-3 border border-[#7f9db9] bg-[#ece9d8] p-3 text-[13px] text-black"
    >
      <p className="font-bold mb-2">Provisioning SharePoint site…</p>
      {submit.viaDemoToggle && (
        <p
          data-testid={testIds.nipe.vaultDropProvisionDemoBadge}
          className="inline-block mb-2 bg-yellow-100 border border-black px-1.5 py-0.5 text-[11px] text-black"
          title="Demo provisioning is enabled for your account"
        >
          Demo provisioning enabled
        </p>
      )}
      {job === undefined && <p>Loading job…</p>}
      {job !== undefined && job !== null && (
        <div data-testid={testIds.nipe.vaultDropProvisionProgress} className="space-y-2">
          <div>
            <span className="font-bold">Status:</span> {job.status}
          </div>
          <div>
            <span className="font-bold">Step:</span> {job.stepsCompleted}/
            {job.stepsTotal} — {job.lastStepLabel}
          </div>
          <div className="w-full bg-gray-300 h-2 border border-black">
            <div className="bg-black h-full" style={{ width: `${pct}%` }} />
          </div>
          {job.status === 'complete' && siteUrl !== null && (
            <div className="pt-1 flex gap-2 items-center">
              <a
                href={siteUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={testIds.nipe.vaultDropProvisionResultUrl}
                className="underline text-blue-900 break-all"
              >
                {siteUrl}
              </a>
              <a
                href={siteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-2 py-0.5 bg-[#d0d0d0] border border-[#404040] text-black font-bold text-[12px] no-underline hover:bg-[#e0e0e0]"
              >
                Open in new tab
              </a>
            </div>
          )}
          {job.status === 'failed' && (
            <div className="pt-1">
              <p
                data-testid={testIds.nipe.vaultDropProvisionError}
                className="text-red-800"
              >
                {job.errorMessage ?? 'Provisioning failed (no error message).'}
              </p>
              <button
                type="button"
                data-testid={testIds.nipe.vaultDropProvisionRetry}
                onClick={retry}
                className="mt-1 px-2 py-0.5 bg-[#ece9d8] border border-[#7f9db9] text-black text-[12px]"
              >
                Retry
              </button>
            </div>
          )}
        </div>
      )}
      <div className="mt-2">
        <button
          type="button"
          data-testid={testIds.nipe.vaultDropProvisionCancel}
          onClick={onClose}
          className="px-3 py-1 bg-[#ece9d8] border border-[#7f9db9] text-black"
        >
          Close
        </button>
      </div>
      {submit.startError !== null && (
        <p
          data-testid={testIds.nipe.vaultDropProvisionError}
          className="text-red-800 text-[12px] mt-1"
        >
          {submit.startError}
        </p>
      )}
    </div>
  );
}
